import { logger } from '../../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { haversineDistance } from '../geo-utils.js'
import type { CompassService } from '../../../services/compass/compass-provider'
import { activityStorage } from '../../../storage/activity-storage.js'
import { featureStorage } from '../../../storage/feature-storage.js'
import type { Feature } from '../../../schemas/feature.js'
import type { ActivityType as SchemaActivityType } from '../../../schemas/activity.js'
import { db } from '../../../database/schema.js'
import { nearestPointOnLine } from '@turf/nearest-point-on-line'
import { lineString, point } from '@turf/helpers'
import { length as turfLength } from '@turf/length'
import { along } from '@turf/along'
import type { ProfilePoint, ProfileWaypoint } from '../elevation-profile.js'
import type { DistanceTimePoint } from '../distance-time-chart.js'
import type { AspectCounts } from '../track-aspect-rose.js'
import { elevationPointService } from '../../../services/terrain/elevation-point-service.js'
import { authService } from '../../../services/auth/auth-service.js'
import { reverseGeocodeService } from '../../../services/geocode/reverse-geocode-service.js'
import { convertDistance } from '../../../utils/units.js'
import { SettingsStorage } from '../../../storage/settings-storage.js'

const log = logger.create('map')

import type { ActivityType } from 'cairn-compass'

export interface TrackPoint {
  lat: number
  lng: number
  timestamp: number
  elevation: number | undefined
  demElevation: number | null
  accuracy: number | undefined
  speed: number | undefined
  activity?: ActivityType | undefined
}

export interface TrackRecordingCallbacks {
  onRecordingChanged: (isRecording: boolean) => void
  showNotification: (
    type: 'success' | 'error',
    title: string,
    message?: string,
    activityId?: string
  ) => void
  navigateToActivity: (activityId: string) => void
  onFeaturesCreated?: (features: Feature[]) => void
  getCurrentLocation?: () => { lat: number; lng: number } | undefined
  onProfileUpdated?: (data: {
    routeProfile: ProfilePoint[]
    trackProfile: ProfilePoint[]
    currentDistance: number | undefined
    waypoints: ProfileWaypoint[]
    distanceTime: DistanceTimePoint[]
    aspectCounts: AspectCounts
  }) => void
}

export class TrackRecordingManager {
  private map: maplibregl.Map | undefined
  private compassService: CompassService

  private currentTrack: TrackPoint[] = []
  private trackSourceId = 'track-recording'
  private trackLayerId = 'track-recording-line'
  private trackStartTime: number | undefined
  private lastLocationTime: number | undefined
  private locationGaps: Array<{ startTime: number; endTime: number; duration: number }> = []

  // Activity created at recording start
  private currentActivityId: string | undefined

  // Route profile data (populated when a matching plan route is found)
  private matchedRouteLine: GeoJSON.Feature<GeoJSON.LineString> | undefined
  private matchedRouteLengthKm = 0
  private routeProfile: ProfilePoint[] = []
  private trackProfile: ProfilePoint[] = []
  private routeWaypoints: ProfileWaypoint[] = []
  private currentRouteDistance: number | undefined
  private distanceTimePoints: DistanceTimePoint[] = []
  private aspectCounts: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
  private cumulativeDistance = 0

  // Health monitoring
  private locationHealthTimer: number | undefined
  locationUpdateCount = 0
  private lastHealthCheck = 0
  private consecutiveMissedIntervals = 0
  private locationHealthHistory: Array<{
    timestamp: number
    updatesPerMinute: number
    health: string
  }> = []

  // Periodic persistence
  private flushTimer: number | undefined
  private static readonly RECORDING_STATE_KEY = 'cairn_recording_state'
  private static readonly TRACK_POINTS_KEY = 'cairn_recording_points'

  private _isRecording = false

  constructor(
    private callbacks: TrackRecordingCallbacks,
    compassService: CompassService
  ) {
    this.compassService = compassService
  }

  get isRecording(): boolean {
    return this._isRecording
  }

  getTrackPoints(): readonly TrackPoint[] {
    return this.currentTrack
  }

  getStartTime(): number | undefined {
    return this.trackStartTime
  }

  /**
   * Check for a recording that was interrupted by WebView recycling.
   * If found, restore the track points and resume recording.
   */
  async tryResumeRecording(): Promise<void> {
    try {
      const stateJson = localStorage.getItem(TrackRecordingManager.RECORDING_STATE_KEY)
      if (!stateJson) {
        return
      }

      const state = JSON.parse(stateJson) as { startTime: number; pointCount: number }

      // Stale if older than 24h — user likely force-closed the app
      if (Date.now() - state.startTime > 24 * 60 * 60 * 1000) {
        this.clearPersistedRecording()
        return
      }

      const pointsJson = localStorage.getItem(TrackRecordingManager.TRACK_POINTS_KEY)
      const restoredPoints: TrackPoint[] = pointsJson ? JSON.parse(pointsJson) : []

      log.debug(
        `🔄 Resuming interrupted recording: ${restoredPoints.length} points from ${new Date(state.startTime).toLocaleTimeString()}`
      )
      this.compassService.logDiagnostic(
        'RECORDING',
        `resumed after WebView recycle: ${restoredPoints.length} points recovered, started ${new Date(state.startTime).toISOString()}`
      )

      this.currentTrack = restoredPoints.sort((a, b) => a.timestamp - b.timestamp)
      this.trackStartTime = state.startTime
      this._isRecording = true
      this.callbacks.onRecordingChanged(true)
      this.startLocationHealthMonitoring()
      this.startPeriodicFlush()

      // Rebuild derived profile data from restored points and emit immediately
      this.rebuildDerivedData()
      this.emitProfileUpdate()

      // Re-add the track source/layer if map is ready
      if (this.map) {
        const coords = this.currentTrack.map(p => [p.lng, p.lat])
        const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords },
        }
        if (this.map.getSource(this.trackSourceId)) {
          ;(this.map.getSource(this.trackSourceId) as maplibregl.GeoJSONSource).setData(geojson)
        } else {
          this.map.addSource(this.trackSourceId, { type: 'geojson', data: geojson })
          this.map.addLayer({
            id: this.trackLayerId,
            type: 'line',
            source: this.trackSourceId,
            paint: { 'line-color': '#ff4444', 'line-width': 4, 'line-opacity': 0.8 },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          })
        }
      }
    } catch (error) {
      log.error('Failed to resume recording:', error)
      this.clearPersistedRecording()
    }
  }

  private persistRecordingState(): void {
    try {
      localStorage.setItem(
        TrackRecordingManager.RECORDING_STATE_KEY,
        JSON.stringify({
          startTime: this.trackStartTime,
          pointCount: this.currentTrack.length,
        })
      )
      localStorage.setItem(
        TrackRecordingManager.TRACK_POINTS_KEY,
        JSON.stringify(this.currentTrack)
      )
    } catch (error) {
      // localStorage quota exceeded — track will survive in memory but not WebView recycling
      log.warn('Failed to persist recording state:', error)
    }
  }

  private clearPersistedRecording(): void {
    localStorage.removeItem(TrackRecordingManager.RECORDING_STATE_KEY)
    localStorage.removeItem(TrackRecordingManager.TRACK_POINTS_KEY)
  }

  private startPeriodicFlush(): void {
    this.stopPeriodicFlush()
    // Flush every 30s so at most 30s of data is lost on WebView crash
    this.flushTimer = window.setInterval(() => {
      if (this._isRecording) {
        this.persistRecordingState()
      }
    }, 30_000)
  }

  private stopPeriodicFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = undefined
    }
  }

  setMap(map: maplibregl.Map): void {
    this.map = map
  }

  async toggle(): Promise<void> {
    log.debug('🔄 Toggle recording called')
    this._isRecording = !this._isRecording
    this.callbacks.onRecordingChanged(this._isRecording)
    this.compassService.logDiagnostic('RECORDING', this._isRecording ? 'started' : 'stopped')
    if (this._isRecording) {
      const { backgroundLocationPrompt } =
        await import('../../../services/permissions/background-location-prompt.js')
      await backgroundLocationPrompt.promptIfNeeded()
      this.startRecording()
    } else {
      await this.stopRecording()
    }
  }

  private startRecording(): void {
    this.currentTrack = []
    this.trackStartTime = Date.now()
    this.lastLocationTime = undefined
    this.locationGaps = []
    this.currentActivityId = undefined
    this.matchedRouteLine = undefined
    this.matchedRouteLengthKm = 0
    this.routeProfile = []
    this.trackProfile = []
    this.routeWaypoints = []
    this.currentRouteDistance = undefined
    this.distanceTimePoints = []
    this.aspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
    this.cumulativeDistance = 0

    this.startLocationHealthMonitoring()
    this.persistRecordingState()
    this.startPeriodicFlush()

    if (this.map) {
      if (this.map.getSource(this.trackSourceId)) {
        ;(this.map.getSource(this.trackSourceId) as maplibregl.GeoJSONSource).setData({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: [] },
        })
      } else {
        this.map.addSource(this.trackSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: [] },
          },
        })
        this.map.addLayer({
          id: this.trackLayerId,
          type: 'line',
          source: this.trackSourceId,
          paint: { 'line-color': '#ff4444', 'line-width': 4, 'line-opacity': 0.8 },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        })
      }
    }

    // Create activity immediately (async, don't block recording start)
    this._startupPromise = this.createActivityAtStart()

    log.debug('📹 Track recording started - health monitoring enabled')
  }

  /** Exposed for testing — resolves when createActivityAtStart + plan matching completes */
  _startupPromise: Promise<void> = Promise.resolve()

  /**
   * Create Activity entity immediately on record start and associate with matching plans.
   * Uses current location for proximity matching against plan routes.
   */
  private async createActivityAtStart(): Promise<void> {
    try {
      const createdBy = authService.getCurrentUserId()
      const result = await activityStorage.createActivity({
        name: 'Recording...',
        activityType: 'hike',
        startTime: new Date().toISOString(),
        origin: { type: 'user-created' },
        createdBy,
      })

      if (!result.success || !result.data) {
        log.error('Failed to create activity at start:', result.error)
        return
      }

      this.currentActivityId = result.data.id
      log.debug(`✅ Activity created at recording start: ${result.data.id}`)

      // Find and link matching plans
      const currentLoc = this.callbacks.getCurrentLocation?.()
      if (currentLoc) {
        await this.matchAndLinkPlans(result.data.id, currentLoc)
      }
    } catch (error) {
      log.error('Failed to create activity at recording start:', error)
    }
  }

  /**
   * Find plans whose time range includes now AND whose route passes near
   * the user's current location (< 50m via nearestPointOnLine).
   * Links to ALL matching plans.
   */
  private async matchAndLinkPlans(
    activityId: string,
    location: { lat: number; lng: number }
  ): Promise<void> {
    try {
      const now = new Date()
      const plans = await db.plans.toArray()
      const userPoint = point([location.lng, location.lat])

      for (const plan of plans) {
        if (plan.times.length === 0) continue

        // Check time overlap: plan date range must include today
        const planDates = plan.times.map(t => new Date(t.datetime))
        const planStart = new Date(Math.min(...planDates.map(d => d.getTime())))
        const planEnd = new Date(Math.max(...planDates.map(d => d.getTime())))

        // Extend planEnd to end of day to match same-day activities
        const planEndOfDay = new Date(planEnd)
        planEndOfDay.setHours(23, 59, 59, 999)

        if (now < planStart || now > planEndOfDay) continue

        // Check spatial proximity against plan features
        const planFeatures = await featureStorage.getFeaturesForEntity('plan', plan.id)
        for (const rf of planFeatures) {
          if (rf.geometry.type !== 'LineString') continue
          const coords = rf.geometry.coordinates as [number, number][]
          if (coords.length < 2) continue

          const line = lineString(coords)
          const nearest = nearestPointOnLine(line, userPoint)
          const distKm = nearest.properties.dist
          const distM = distKm * 1000

          if (distM < 50) {
            log.debug(
              `🔗 Plan "${plan.name}" is ${distM.toFixed(0)}m from current location — linking`
            )
            await activityStorage.linkToPlan(activityId, plan.id)

            // Capture the first matched line for elevation profile
            if (!this.matchedRouteLine) {
              this.matchedRouteLine = line
              this.buildRouteProfile()
              await this.findPlanWaypoints(plan.id)
              this.emitProfileUpdate()
            }
            break
          }
        }
      }
    } catch (error) {
      log.error('Failed to match plans at recording start:', error)
    }
  }

  /**
   * Sample the matched route at regular intervals to build the elevation profile.
   * Uses Z coordinates from the route geometry (from GPX import or DEM).
   */
  private buildRouteProfile(): void {
    if (!this.matchedRouteLine) return

    const coords = this.matchedRouteLine.geometry.coordinates
    this.matchedRouteLengthKm = turfLength(this.matchedRouteLine, { units: 'kilometers' })
    const totalMeters = this.matchedRouteLengthKm * 1000

    // Sample every ~25m or at least 200 points, whichever gives more density
    const numSamples = Math.max(200, Math.ceil(totalMeters / 25))
    const stepKm = this.matchedRouteLengthKm / numSamples

    const profile: ProfilePoint[] = []
    for (let i = 0; i <= numSamples; i++) {
      const distKm = i * stepKm
      const pt = along(this.matchedRouteLine, distKm, { units: 'kilometers' })
      // Get elevation: interpolate from nearest route coordinates with Z values
      const elev = this.interpolateElevation(pt.geometry.coordinates, coords)
      profile.push({ distance: distKm * 1000, elevation: elev })
    }

    this.routeProfile = profile
    log.debug(`📊 Route profile built: ${profile.length} points over ${totalMeters.toFixed(0)}m`)
  }

  /**
   * Interpolate elevation at a point from route coordinates with Z values.
   * Finds the two nearest route vertices and linearly interpolates.
   */
  private interpolateElevation(targetCoord: number[], routeCoords: number[][]): number {
    // If route has no Z values, return 0
    if (routeCoords.length === 0 || routeCoords[0].length < 3) return 0

    let minDist = Infinity
    let nearestIdx = 0
    for (let i = 0; i < routeCoords.length; i++) {
      const dx = routeCoords[i][0] - targetCoord[0]
      const dy = routeCoords[i][1] - targetCoord[1]
      const d = dx * dx + dy * dy
      if (d < minDist) {
        minDist = d
        nearestIdx = i
      }
    }

    // Simple: return nearest vertex elevation
    return routeCoords[nearestIdx][2] ?? 0
  }

  /**
   * Find point features on the plan that serve as waypoints.
   */
  private async findPlanWaypoints(planId: string): Promise<void> {
    if (!this.matchedRouteLine) return

    try {
      const features = await featureStorage.getFeaturesForEntity('plan', planId)
      const waypoints: ProfileWaypoint[] = []

      for (const f of features) {
        if (f.geometry.type !== 'Point') continue
        const fCoords = f.geometry.coordinates
        const fPoint = point([fCoords[0], fCoords[1]])
        const nearest = nearestPointOnLine(this.matchedRouteLine, fPoint)
        const distM = nearest.properties.dist * 1000

        if (distM < 50) {
          const distAlongM = nearest.properties.location * 1000
          waypoints.push({
            name: f.name || 'Waypoint',
            distance: distAlongM,
            elevation: fCoords[2] ?? 0,
          })
        }
      }

      this.routeWaypoints = waypoints
      log.debug(`Found ${waypoints.length} waypoints on plan`)
    } catch (error) {
      log.error('Failed to find plan waypoints:', error)
    }
  }

  /**
   * Rebuild distance-time, aspect, and track elevation data from the full currentTrack.
   * Called when derived arrays are empty but track points exist (e.g., hot reload during recording).
   */
  private rebuildDerivedData(): void {
    if (this.currentTrack.length < 2) return

    this.distanceTimePoints = []
    this.aspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
    this.cumulativeDistance = 0
    this.trackProfile = []

    for (let i = 0; i < this.currentTrack.length; i++) {
      const tp = this.currentTrack[i]

      if (i > 0) {
        const prev = this.currentTrack[i - 1]
        const segDist = haversineDistance(prev.lat, prev.lng, tp.lat, tp.lng)
        this.cumulativeDistance += segDist
      }

      const elapsed = this.trackStartTime ? (tp.timestamp - this.trackStartTime) / 1000 : 0
      this.distanceTimePoints.push({ elapsed, distance: this.cumulativeDistance })

      // Rebuild track elevation profile
      if (this.matchedRouteLine) {
        const tpPoint = point([tp.lng, tp.lat])
        const nearest = nearestPointOnLine(this.matchedRouteLine, tpPoint)
        const distOffRouteM = nearest.properties.dist * 1000
        if (distOffRouteM <= 100) {
          const distAlongM = nearest.properties.location * 1000
          this.currentRouteDistance = distAlongM
          this.trackProfile.push({
            distance: distAlongM,
            elevation: tp.elevation ?? 0,
          })
        }
      } else {
        this.trackProfile.push({
          distance: this.cumulativeDistance,
          elevation: tp.elevation ?? 0,
        })
      }
    }

    // Rebuild aspect counts from DEM slope data
    void this.rebuildAspectCounts()

    log.debug(`🔄 Rebuilt derived data from ${this.currentTrack.length} track points`)
  }

  private async rebuildAspectCounts(): Promise<void> {
    const counts: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
    const aspects = await Promise.all(
      this.currentTrack.map(tp => elevationPointService.getAspect(tp.lat, tp.lng))
    )
    for (const aspect of aspects) {
      if (aspect !== null) {
        counts[Math.round(aspect / 45) % 8]++
      }
    }
    this.aspectCounts = counts
    this.emitProfileUpdate()
  }

  private emitProfileUpdate(): void {
    // Backfill derived data if track exists but arrays are empty (hot reload mid-recording)
    if (this.currentTrack.length >= 2 && this.distanceTimePoints.length === 0) {
      this.rebuildDerivedData()
    }

    log.debug(
      `📊 Profile update: route=${this.routeProfile.length} track=${this.trackProfile.length}`
    )
    this.callbacks.onProfileUpdated?.({
      routeProfile: [...this.routeProfile],
      trackProfile: [...this.trackProfile],
      currentDistance: this.currentRouteDistance,
      waypoints: [...this.routeWaypoints],
      distanceTime: [...this.distanceTimePoints],
      aspectCounts: [...this.aspectCounts] as AspectCounts,
    })
  }

  private async stopRecording(): Promise<void> {
    if (this.currentTrack.length > 0) {
      const duration = this.trackStartTime ? (Date.now() - this.trackStartTime) / 1000 : 0
      const distance = this.calculateTrackDistance()

      log.debug(
        `⏹️ Track recording stopped - ${this.currentTrack.length} points, ${distance.toFixed(1)}m, ${duration.toFixed(0)}s`
      )

      if (this.locationGaps.length > 0) {
        log.warn(`⚠️ Track had ${this.locationGaps.length} gaps:`)
        this.locationGaps.forEach((gap, index) => {
          log.warn(
            `  Gap ${index + 1}: ${gap.duration.toFixed(1)}s (${new Date(gap.startTime).toLocaleTimeString()} - ${new Date(gap.endTime).toLocaleTimeString()})`
          )
        })

        const totalGapTime = this.locationGaps.reduce((sum, gap) => sum + gap.duration, 0)
        log.warn(
          `  Total gap time: ${totalGapTime.toFixed(1)}s of ${duration.toFixed(0)}s recording`
        )
      } else {
        log.debug('✅ Track recording completed with no gaps detected')
      }

      await this.saveTrackAsActivity()
    } else {
      log.debug('⏹️ Recording stopped - no track data')
    }

    this.trackStartTime = undefined
    this.lastLocationTime = undefined
    this.currentActivityId = undefined
    this.matchedRouteLine = undefined
    this.routeProfile = []
    this.trackProfile = []
    this.routeWaypoints = []
    this.currentRouteDistance = undefined
    this.distanceTimePoints = []
    this.aspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
    this.cumulativeDistance = 0
    this.emitProfileUpdate()
    this.stopLocationHealthMonitoring()
    this.stopPeriodicFlush()
    this.clearPersistedRecording()
  }

  addTrackPoint(location: {
    latitude: number
    longitude: number
    altitude: number | undefined
    accuracy: number | undefined
    speed?: number | undefined
    activity?: ActivityType
    timestamp?: number
    buffered?: boolean
  }): void {
    // Reject coarse location fixes (cell tower, Wi-Fi triangulation)
    if (location.accuracy !== undefined && location.accuracy >= 100) {
      log.warn(`⚠️ Rejecting coarse location fix: accuracy ${location.accuracy.toFixed(0)}m`)
      return
    }

    // Use GPS timestamp when available (critical for buffered points from screen-off),
    // fall back to wall-clock time for real-time points
    const pointTime = location.timestamp ?? Date.now()

    // Reject points from before recording started. The native plugin buffers
    // every GPS fix to a JSONL file that persists across process death, and
    // flushBufferedLocations() drains the entire file on resume — including
    // points captured before the user pressed Record. The isRecording gate in
    // cairn-map.ts catches fresh points but can't reject stale buffered ones
    // that land while recording is already active.
    if (this.trackStartTime && pointTime < this.trackStartTime) {
      if (location.buffered) {
        log.debug(
          `⏭️  Skipping buffered point from before recording start ` +
            `(point: ${new Date(pointTime).toISOString()}, ` +
            `started: ${new Date(this.trackStartTime).toISOString()})`
        )
      }
      return
    }

    // Detect location gaps (>10 seconds without update)
    if (this.lastLocationTime && pointTime - this.lastLocationTime > 10000) {
      const gapDuration = (pointTime - this.lastLocationTime) / 1000
      log.warn(`⚠️ Location gap detected: ${gapDuration.toFixed(1)}s`)

      this.locationGaps.push({
        startTime: this.lastLocationTime,
        endTime: pointTime,
        duration: gapDuration,
      })
    }

    // Reject stale/cached GPS fixes that jump impossibly far from the previous point
    if (this.currentTrack.length > 0) {
      const prev = this.currentTrack[this.currentTrack.length - 1]
      const dist = haversineDistance(prev.lat, prev.lng, location.latitude, location.longitude)
      const timeDelta = (pointTime - prev.timestamp) / 1000
      // 50 m/s ≈ 180 km/h — impossible on foot, catches stale fixes
      if (timeDelta > 0 && timeDelta < 30 && dist / timeDelta > 50) {
        log.warn(
          `⚠️ Rejecting stale fix: ${dist.toFixed(0)}m in ${timeDelta.toFixed(1)}s (${(dist / timeDelta).toFixed(0)} m/s)`
        )
        return
      }
    }

    const trackPoint: TrackPoint = {
      lat: location.latitude,
      lng: location.longitude,
      timestamp: pointTime,
      elevation: location.altitude,
      demElevation: null,
      accuracy: location.accuracy,
      speed: location.speed,
      activity: location.activity,
    }

    // Fire-and-forget DEM elevation lookup — fast if tile is cached, null if not
    void elevationPointService.getElevation(location.latitude, location.longitude).then(demElev => {
      trackPoint.demElevation = demElev
    })

    // Insert in timestamp order — buffered points from screen-off may arrive late
    const lastTs =
      this.currentTrack.length > 0 ? this.currentTrack[this.currentTrack.length - 1].timestamp : 0
    if (pointTime >= lastTs) {
      this.currentTrack.push(trackPoint)
    } else {
      // Out-of-order: insert at correct position and force derived data rebuild
      let idx = this.currentTrack.length - 1
      while (idx > 0 && this.currentTrack[idx - 1].timestamp > pointTime) idx--
      this.currentTrack.splice(idx, 0, trackPoint)
      // Invalidate derived data so next emit rebuilds from scratch
      this.distanceTimePoints = []
    }

    try {
      if (this.map) {
        const coords = this.currentTrack.map(p => [p.lng, p.lat])
        const geojson: GeoJSON.Feature<GeoJSON.LineString> = {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords },
        }
        if (this.map.getSource(this.trackSourceId)) {
          ;(this.map.getSource(this.trackSourceId) as maplibregl.GeoJSONSource).setData(geojson)
        } else {
          // Source was removed (style change) or never created — recreate it
          this.map.addSource(this.trackSourceId, { type: 'geojson', data: geojson })
          this.map.addLayer({
            id: this.trackLayerId,
            type: 'line',
            source: this.trackSourceId,
            paint: { 'line-color': '#ff4444', 'line-width': 4, 'line-opacity': 0.8 },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          })
        }
      }
    } catch (err) {
      log.warn('⚠️ Failed to update track layer:', err)
    }

    // Detect suspicious large jumps between points (>200m in 2 seconds = 100m/s = 360km/h)
    if (this.currentTrack.length > 1) {
      const prevPoint = this.currentTrack[this.currentTrack.length - 2]
      const distance = haversineDistance(
        prevPoint.lat,
        prevPoint.lng,
        trackPoint.lat,
        trackPoint.lng
      )
      const timeDelta = (pointTime - prevPoint.timestamp) / 1000

      if (distance > 200 && timeDelta < 10) {
        log.warn(
          `⚠️ Large jump detected: ${distance.toFixed(0)}m in ${timeDelta.toFixed(1)}s (${((distance / timeDelta) * 3.6).toFixed(0)} km/h)`
        )
      }
    }

    this.locationUpdateCount++

    // Update distance-time and aspect data
    if (this.currentTrack.length >= 2) {
      const prev = this.currentTrack[this.currentTrack.length - 2]
      const segDist = haversineDistance(prev.lat, prev.lng, trackPoint.lat, trackPoint.lng)
      this.cumulativeDistance += segDist
    }

    // Slope aspect from DEM at current point
    void elevationPointService.getAspect(trackPoint.lat, trackPoint.lng).then(aspect => {
      if (aspect !== null) {
        this.aspectCounts[Math.round(aspect / 45) % 8]++
      }
    })
    const elapsed = this.trackStartTime ? (pointTime - this.trackStartTime) / 1000 : 0
    this.distanceTimePoints.push({ elapsed, distance: this.cumulativeDistance })

    // Update elevation profile data if we have a matched route
    if (this.matchedRouteLine) {
      this.updateTrackOnRoute(trackPoint)
    } else {
      this.updateStandaloneTrackProfile(trackPoint)
    }

    log.debug(
      `📍 Track point recorded: ${this.currentTrack.length} points (±${location.accuracy?.toFixed(1)}m)`
    )
    this.lastLocationTime = pointTime
  }

  /**
   * Snap a track point to the matched route and update the track profile
   * and current position distance.
   */
  private updateTrackOnRoute(tp: TrackPoint): void {
    if (!this.matchedRouteLine) return

    const tpPoint = point([tp.lng, tp.lat])
    const nearest = nearestPointOnLine(this.matchedRouteLine, tpPoint)
    const distOffRouteM = nearest.properties.dist * 1000

    // Only include points within 100m of the route
    if (distOffRouteM > 100) return

    const distAlongM = nearest.properties.location * 1000
    this.currentRouteDistance = distAlongM

    this.trackProfile.push({
      distance: distAlongM,
      elevation: tp.elevation ?? 0,
    })

    // Throttle profile updates to every 5th point
    if (this.currentTrack.length % 5 === 0 || this.currentTrack.length <= 3) {
      this.emitProfileUpdate()
    }
  }

  /**
   * When there's no matched route, build the elevation profile from the track itself.
   * X-axis is cumulative distance traveled.
   */
  private updateStandaloneTrackProfile(tp: TrackPoint): void {
    let cumulativeDist = 0
    if (this.trackProfile.length > 0 && this.currentTrack.length >= 2) {
      const prev = this.currentTrack[this.currentTrack.length - 2]
      cumulativeDist =
        this.trackProfile[this.trackProfile.length - 1].distance +
        haversineDistance(prev.lat, prev.lng, tp.lat, tp.lng)
    }

    this.trackProfile.push({
      distance: cumulativeDist,
      elevation: tp.elevation ?? 0,
    })

    // Throttle profile updates to every 5th point
    if (this.currentTrack.length % 5 === 0 || this.currentTrack.length <= 3) {
      this.emitProfileUpdate()
    }
  }

  // --- Activity saving ---

  private async generateActivityName(distance: number): Promise<string | undefined> {
    const settings = new SettingsStorage().getSettingsWithDefaults()
    const distStr = convertDistance(distance, settings.unitSystem)

    const startPoint = this.currentTrack[0]
    if (startPoint) {
      try {
        const geo = await reverseGeocodeService.lookup(startPoint.lat, startPoint.lng)
        if (geo?.displayName) {
          return `${geo.displayName} (${distStr})`
        }
      } catch {
        // Offline — leave name undefined for later retry
      }
    }

    return undefined
  }

  /**
   * Map compass activity detection to schema ActivityType.
   * The compass reports motion types (walking, running, cycling, etc.)
   * while the schema uses outing categories (hike, run, bike, etc.).
   */
  private resolveActivityType(): SchemaActivityType {
    // Count activity types from track points (ignoring unknown/determining/stationary)
    const counts = new Map<string, number>()
    for (const point of this.currentTrack) {
      if (
        point.activity &&
        point.activity !== 'unknown' &&
        point.activity !== 'determining' &&
        point.activity !== 'stationary'
      ) {
        counts.set(point.activity, (counts.get(point.activity) ?? 0) + 1)
      }
    }

    // Find the most common activity
    let dominant: string | undefined
    let maxCount = 0
    for (const [activity, count] of counts) {
      if (count > maxCount) {
        dominant = activity
        maxCount = count
      }
    }

    switch (dominant) {
      case 'walking':
        return 'hike'
      case 'running':
        return 'run'
      case 'cycling':
        return 'bike'
      default:
        return 'hike'
    }
  }

  private trackToLineString(): GeoJSON.LineString {
    if (this.currentTrack.length < 2) {
      throw new Error('Track must have at least 2 points')
    }

    const coordinates = this.currentTrack.map(point => {
      const coord: number[] = [point.lng, point.lat]
      if (point.elevation !== undefined) {
        coord.push(point.elevation)
      }
      return coord
    })

    return {
      type: 'LineString',
      coordinates,
    }
  }

  /** Per-coordinate timestamps in ms epoch — preserved on the saved Feature for pace analysis */
  private trackTimestamps(): number[] {
    return this.currentTrack.map(p => p.timestamp)
  }

  private async saveTrackAsActivity(): Promise<void> {
    if (this.currentTrack.length < 2) {
      log.warn('⚠️ Cannot save track - insufficient points')
      this.callbacks.showNotification(
        'error',
        'Cannot save track - insufficient GPS points recorded'
      )
      return
    }

    try {
      const trackEndTime = new Date()
      const duration = this.trackStartTime ? (Date.now() - this.trackStartTime) / 1000 : 0
      const distance = this.calculateTrackDistance()
      const geometry = this.trackToLineString()

      const activityName = await this.generateActivityName(distance)
      const activityType = this.resolveActivityType()
      const createdBy = authService.getCurrentUserId()

      let activityId = this.currentActivityId

      if (activityId) {
        // Update the activity created at recording start
        const updateResult = await activityStorage.updateActivity(activityId, {
          name: activityName,
          activityType,
          endTime: trackEndTime.toISOString(),
        })
        if (!updateResult.success) {
          log.warn('Failed to update activity, will create new one:', updateResult.error)
          activityId = undefined
        } else {
          log.debug(`✅ Updated activity: ${activityName} (ID: ${activityId})`)
        }
      }

      if (!activityId) {
        // Fallback: create activity if it wasn't created at start (e.g. resumed recording)
        const trackStartTime = this.trackStartTime ? new Date(this.trackStartTime) : new Date()
        const activityResult = await activityStorage.createActivity({
          name: activityName,
          activityType,
          startTime: trackStartTime.toISOString(),
          endTime: trackEndTime.toISOString(),
          origin: { type: 'user-created' },
          createdBy,
        })
        if (!activityResult.success || !activityResult.data) {
          throw new Error(`Failed to create activity: ${activityResult.error?.message}`)
        }
        activityId = activityResult.data.id
        log.debug(`✅ Created activity (fallback): ${activityName} (ID: ${activityId})`)
      }

      // Backfill DEM elevations for any points that were null at capture time
      const demElevations = this.currentTrack.map(p => p.demElevation)
      const nullDemIndices = demElevations.map((e, i) => (e === null ? i : -1)).filter(i => i >= 0)
      if (nullDemIndices.length > 0) {
        const backfillResults = await Promise.all(
          nullDemIndices.map(i => {
            const pt = this.currentTrack[i]
            return elevationPointService.getElevation(pt.lat, pt.lng)
          })
        )
        for (let j = 0; j < nullDemIndices.length; j++) {
          demElevations[nullDemIndices[j]] = backfillResults[j]
        }
      }

      const trackName = `Track ${Math.round(distance)}m (${Math.round(duration / 60)}min)`
      const featureResult = await featureStorage.createFeature(
        {
          name: trackName,
          type: 'track',
          geometry,
          properties: {
            coordTimes: this.trackTimestamps(),
            coordAccuracies: this.currentTrack.map(p => p.accuracy ?? null),
            coordActivities: this.currentTrack.map(p => p.activity ?? null),
            coordSpeeds: this.currentTrack.map(p => p.speed ?? null),
            coordDemElevations: demElevations,
          },
          tags: ['track'],
          createdBy,
        },
        {
          entityType: 'activity',
          entityId: activityId,
        }
      )
      if (!featureResult.success || !featureResult.data) {
        throw new Error(`Failed to create feature: ${featureResult.error?.message}`)
      }

      const feature = featureResult.data
      log.debug(`✅ Created track feature: ${feature.name} (ID: ${feature.id})`)

      // Render the new feature on the map
      if (this.callbacks.onFeaturesCreated) {
        this.callbacks.onFeaturesCreated([feature])
      }

      this.callbacks.showNotification(
        'success',
        'Track saved successfully!',
        `Activity created: ${activityName}`,
        activityId
      )
    } catch (error) {
      log.error('❌ Failed to save track as activity:', error)
      this.callbacks.showNotification(
        'error',
        'Failed to save track',
        `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // --- Health monitoring ---

  private startLocationHealthMonitoring(): void {
    this.locationUpdateCount = 0
    this.lastHealthCheck = Date.now()
    this.consecutiveMissedIntervals = 0
    this.locationHealthHistory = []

    this.locationHealthTimer = window.setInterval(() => {
      this.checkLocationHealth().catch(err => {
        log.error('Health check failed:', err)
      })
    }, 30000)

    log.debug('🏥 Location health monitoring started')
  }

  private stopLocationHealthMonitoring(): void {
    if (this.locationHealthTimer) {
      clearInterval(this.locationHealthTimer)
      this.locationHealthTimer = undefined
      log.debug('🏥 Location health monitoring stopped')
    }
  }

  private async checkLocationHealth(): Promise<void> {
    const now = Date.now()
    const timeElapsed = (now - this.lastHealthCheck) / 1000
    const expectedUpdates = Math.floor(timeElapsed / 5)
    const actualUpdates = this.locationUpdateCount
    const updatesPerMinute = (actualUpdates / timeElapsed) * 60

    this.locationUpdateCount = 0
    this.lastHealthCheck = now

    let healthStatus: string
    let needsIntervention = false

    if (actualUpdates === 0) {
      healthStatus = 'CRITICAL'
      needsIntervention = true
      this.consecutiveMissedIntervals++
    } else if (actualUpdates < expectedUpdates * 0.5) {
      healthStatus = 'POOR'
      needsIntervention = true
      this.consecutiveMissedIntervals++
    } else if (actualUpdates < expectedUpdates * 0.8) {
      healthStatus = 'DEGRADED'
      this.consecutiveMissedIntervals = 0
    } else {
      healthStatus = 'GOOD'
      this.consecutiveMissedIntervals = 0
    }

    this.locationHealthHistory.push({
      timestamp: now,
      updatesPerMinute,
      health: healthStatus,
    })

    if (this.locationHealthHistory.length > 20) {
      this.locationHealthHistory = this.locationHealthHistory.slice(-20)
    }

    log.debug(
      `🏥 Location health: ${healthStatus} (${actualUpdates}/${expectedUpdates} updates, ${updatesPerMinute.toFixed(1)}/min)`
    )

    if (needsIntervention) {
      await this.attemptLocationRecovery(healthStatus)
    }
  }

  private async attemptLocationRecovery(healthStatus: string): Promise<void> {
    log.warn(`🔧 Attempting location recovery for ${healthStatus} health...`)

    try {
      if (this.consecutiveMissedIntervals >= 3) {
        log.warn('🚨 Critical location failure detected - restarting service')
        await this.restartLocationService()

        try {
          const cap = (window as unknown as Record<string, unknown>).Capacitor as
            | { Plugins?: { Haptics?: { impact: (opts: { style: string }) => Promise<void> } } }
            | undefined
          await cap?.Plugins?.Haptics?.impact({ style: 'heavy' })
        } catch (e) {
          // Haptic not available
        }
      } else {
        log.warn('⚠️ Requesting fresh location update...')
        await this.requestFreshLocation()
      }
    } catch (error) {
      log.error('Failed to recover location service:', error)

      if (this.consecutiveMissedIntervals >= 5) {
        this.alertUserLocationFailure()
      }
    }
  }

  private async restartLocationService(): Promise<void> {
    const timeout = (ms: number): Promise<never> =>
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))

    try {
      await Promise.race([this.compassService.stopLocationTracking(), timeout(5000)])
      await new Promise(resolve => setTimeout(resolve, 2000))
      await Promise.race([
        this.compassService.startLocationTracking({
          interval: 5,
          priority: 'high',
          displacement: 2,
          enableForegroundService: true,
        }),
        timeout(10000),
      ])
      log.debug('✅ Location service restarted successfully')
    } catch (error) {
      log.error('❌ Failed to restart location service:', error)
      throw error
    }
  }

  private async requestFreshLocation(): Promise<void> {
    log.debug('🔄 Requesting fresh location update...')
  }

  private alertUserLocationFailure(): void {
    log.error('🚨 Persistent location failure - user intervention needed')
    this.callbacks.showNotification(
      'error',
      'GPS tracking interrupted',
      'Check battery optimization settings or restart the app'
    )
  }

  private calculateTrackDistance(): number {
    let totalDistance = 0
    for (let i = 1; i < this.currentTrack.length; i++) {
      const prev = this.currentTrack[i - 1]
      const curr = this.currentTrack[i]
      totalDistance += haversineDistance(prev.lat, prev.lng, curr.lat, curr.lng)
    }
    return totalDistance
  }

  dispose(): void {
    this.stopLocationHealthMonitoring()
    this.stopPeriodicFlush()
  }
}
